import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';
import { browserName, deviceKind, diagnose, type AlertDiagnosis } from './platform';

// What this phone can do for the app: is it opened from the home screen, may it show
// notifications, and a real test notification. Install handling follows the portal's
// useInstallPrompt (apps/portal/src/hooks/useInstallPrompt.ts).

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Android fires this once, often before React has mounted, so catch it at load.
let installEvent: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<() => void>();
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  installEvent = e as BeforeInstallPromptEvent;
  installListeners.forEach((fn) => fn());
});

export type NotifyState = NotificationPermission | 'unsupported';

export const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const isAndroid = /Android/i.test(navigator.userAgent);
/** A phone or tablet, where the app must live on the home screen with notifications on. */
export const isPhone = isIOS || isAndroid;
/** Mac, Windows, iPhone or Android, for the step-by-step fix when a test alert is not seen. */
export const thisDevice = deviceKind({ userAgent: navigator.userAgent, platform: navigator.platform, maxTouchPoints: navigator.maxTouchPoints });
export const thisBrowser = browserName(navigator.userAgent);

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function notifyState(): NotifyState {
  return 'Notification' in window ? Notification.permission : 'unsupported';
}

export function useDevice() {
  const [standalone, setStandalone] = useState(isStandalone);
  const [notify, setNotify] = useState<NotifyState>(notifyState);
  const [canInstall, setCanInstall] = useState(installEvent !== null);

  useEffect(() => {
    const onInstallable = () => setCanInstall(true);
    installListeners.add(onInstallable);
    const media = window.matchMedia('(display-mode: standalone)');
    const onMode = () => setStandalone(isStandalone());
    media.addEventListener('change', onMode);
    // Permission can change in the phone's settings while the app is in the background.
    const onVisible = () => { if (document.visibilityState === 'visible') setNotify(notifyState()); };
    document.addEventListener('visibilitychange', onVisible);
    const onChanged = () => setNotify(notifyState());
    window.addEventListener('chats-notify-changed', onChanged);
    return () => {
      installListeners.delete(onInstallable);
      media.removeEventListener('change', onMode);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('chats-notify-changed', onChanged);
    };
  }, []);

  const install = useCallback(async () => {
    if (!installEvent) return false;
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    installEvent = null;
    setCanInstall(false);
    return outcome === 'accepted';
  }, []);

  /** Must be called from a tap: iPhone ignores the request otherwise. */
  const askNotifications = useCallback(async () => {
    if (!('Notification' in window)) return 'unsupported' as const;
    const result = await Notification.requestPermission();
    setNotify(result);
    // Every screen's copy hears it at once (review, 19 Oct: the app shell's copy stayed "not asked" until the app
    // went to the background, so the phone was not registered and a test tap was lost).
    window.dispatchEvent(new Event('chats-notify-changed'));
    return result;
  }, []);

  return { standalone, notify, canInstall, install, askNotifications };
}

export interface TestResult {
  ok: boolean;
  /** How it was shown, or the exact error, for the Phone check in Settings. */
  detail: string;
}

/** Shows a real notification on this phone, the same way a new lead will arrive. */
export async function showTestNotification(): Promise<TestResult> {
  if (notifyState() !== 'granted') return { ok: false, detail: `Permission is ${notifyState()}` };
  const title = 'New lead in Kenridge';
  const options: NotificationOptions = {
    body: 'Mike T.: Hi, how much for a king mattress? (test)',
    icon: '/icon-192.png',
    badge: '/icon-192.png',
    tag: `cmb-chats-test-${Date.now()}`,
    data: { url: '/' },
  };
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.showNotification(title, options);
        return { ok: true, detail: 'Shown by the service worker' };
      }
    }
    new Notification(title, options);
    return { ok: true, detail: 'Shown by the page (no service worker)' };
  } catch (e) {
    return { ok: false, detail: e instanceof Error ? `${e.name}: ${e.message}` : String(e) };
  }
}

export interface PhoneFacts {
  device: string;
  standalone: boolean;
  permission: NotifyState;
  serviceWorker: string;
  push: string;
}

/** Plain facts about this phone, shown in Settings so a problem can be read off one screen. */
export async function phoneFacts(): Promise<PhoneFacts> {
  const ios = navigator.userAgent.match(/OS (\d+)_(\d+)/);
  const device = isIOS ? `iPhone, iOS ${ios ? `${ios[1]}.${ios[2]}` : 'unknown'}` : isAndroid ? 'Android' : 'Computer';
  let serviceWorker = 'Not supported';
  if ('serviceWorker' in navigator) {
    const reg = await navigator.serviceWorker.getRegistration();
    serviceWorker = !reg ? 'Not registered' : reg.active ? 'Running' : reg.installing || reg.waiting ? 'Starting' : 'Registered, not running';
  }
  const push = await pushState();
  return { device, standalone: isStandalone(), permission: notifyState(), serviceWorker, push };
}

/** Why a test alert might not have been seen, from what the browser tells the app for sure. */
export async function alertDiagnosis(): Promise<AlertDiagnosis> {
  let registered: boolean | null = null;
  try {
    // No push key in this build (local preview): only the permission can be judged.
    if (VAPID_PUBLIC_KEY && 'serviceWorker' in navigator && 'PushManager' in window) {
      const reg = await navigator.serviceWorker.getRegistration();
      registered = !!(await reg?.pushManager.getSubscription());
    }
  } catch { /* cannot tell: judge on the permission alone */ }
  return diagnose(notifyState(), registered);
}

// ---------------------------------------------------------------- push (app closed)

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

function keyBytes(base64url: string): ArrayBuffer {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes.buffer;
}

function encodeKey(buf: ArrayBuffer | null): string {
  if (!buf) return '';
  return btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Whether this phone will get notifications with the app closed, in words for the Phone check. */
async function pushState(): Promise<string> {
  if (!('PushManager' in window) || !('serviceWorker' in navigator)) return 'Not supported';
  if (!VAPID_PUBLIC_KEY) return 'Supported, not set up yet';
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'Registered for this login' : 'Supported, not registered';
}

/**
 * Real activity on this phone for head office's readiness board: opened the app, opened an alert (the notification
 * link carries from=push), tapped a test alert (test=1). Read from the address; the markers come off only once
 * Sentinel has saved them (review, 19 Oct), so a failed save is tried again next time.
 */
function readActivity(): { last_push_opened_at: boolean; test_opened_at: boolean } {
  const params = new URLSearchParams(window.location.search);
  const fromPush = params.get('from') === 'push';
  return { last_push_opened_at: fromPush, test_opened_at: fromPush && params.get('test') === '1' };
}

function clearActivityMarkers() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('from') && !params.has('test')) return;
  params.delete('from');
  params.delete('test');
  const rest = params.toString();
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
}

/** The app's background worker, or null after 10 seconds (review, 19 Oct: on bad signal it never became ready). */
async function workerReady(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 10_000)),
  ]);
}

/**
 * Registers this phone so a new message reaches it with the app closed, and records that it was opened. Saved by
 * Sentinel (messaging-send register_device), so a phone belongs to whoever is signed in on it now. Runs once alerts
 * are allowed and each time the app comes back to the front; safe to repeat.
 */
export async function registerPush(userId: string): Promise<boolean> {
  if (!VAPID_PUBLIC_KEY || notifyState() !== 'granted') return false;
  if (!('PushManager' in window)) return false;
  try {
    const reg = await workerReady();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription()
      ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(VAPID_PUBLIC_KEY) });
    const activity = readActivity();
    const { data, error } = await supabase.functions.invoke('messaging-send', {
      body: {
        action: 'register_device', endpoint: sub.endpoint, p256dh: encodeKey(sub.getKey('p256dh')), auth: encodeKey(sub.getKey('auth')),
        user_agent: navigator.userAgent.slice(0, 300), activity,
      },
    });
    if (error || !(data as { ok?: boolean } | null)?.ok) return false;
    if (activity.test_opened_at) markTestTapped(userId);
    clearActivityMarkers();
    return true;
  } catch {
    return false;
  }
}

/** This device's own key for the open record, kept in its storage; a new one if storage is blocked. */
function deviceKey(): string {
  try {
    const saved = localStorage.getItem('chats.deviceKey');
    if (saved && /^[A-Za-z0-9-]{16,64}$/.test(saved)) return saved;
    const made = crypto.randomUUID();
    localStorage.setItem('chats.deviceKey', made);
    return made;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * Records that Chats was opened on this device, with or without alerts (Gary, 22 Oct 2026): head office sees who uses
 * Chats on a phone or a computer, and whether alerts were allowed at that moment. Never asks for anything and never
 * slows the app: the push address is read only if the device already has one.
 */
export async function reportOpen(): Promise<void> {
  try {
    let endpoint: string | null = null;
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      const reg = await navigator.serviceWorker.getRegistration();
      endpoint = (await reg?.pushManager.getSubscription())?.endpoint ?? null;
    }
    await supabase.functions.invoke('messaging-send', {
      body: {
        action: 'device_open', device_key: deviceKey(), user_agent: navigator.userAgent.slice(0, 300),
        installed: isStandalone(), embedded: EMBEDDED, permission: notifyState(), endpoint,
      },
    });
  } catch { /* the record is for head office only; the app carries on */ }
}

/** Signing out takes this device off the person's alerts, so the next person to sign in here gets them. */
export async function unregisterPush(): Promise<void> {
  // Never slows sign-out (review, 19 Oct): inside Sentinel there is nothing to remove; the worker is looked up without
  // waiting, and Sentinel gets at most 3 seconds.
  if (EMBEDDED || !('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    await Promise.race([
      supabase.functions.invoke('messaging-send', { body: { action: 'unregister_device', endpoint: sub.endpoint } }),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]);
    await sub.unsubscribe();
  } catch { /* signed out anyway */ }
}

function useMedia(query: string): boolean {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const change = () => setOn(m.matches);
    change();
    m.addEventListener('change', change);
    return () => m.removeEventListener('change', change);
  }, [query]);
  return on;
}

/** A computer screen: the list and the open chat side by side (Gary, 15 Oct). */
export function useWide(): boolean {
  // A tablet counts by its short side too, so it keeps one layout when turned (16 Oct). No
  // phone is 700px on both sides; a narrow computer window stays in the phone layout.
  return useMedia('(min-width: 1000px), (pointer: coarse) and (min-width: 700px) and (min-height: 700px)');
}

/** Wide enough for the Info panel beside the chat too. */
export function useInfoPanel(): boolean {
  return useMedia('(min-width: 1280px)');
}

/**
 * Chats inside Sentinel (built with CHATS_BASE=/chats/). It can never be installed or alert on its own, so a phone
 * is never held at setup there (Gary, 19 Oct: Debbie looped on the setup page); it points to the stand-alone app.
 */
export const EMBEDDED = import.meta.env.BASE_URL !== '/';

/** The stand-alone CMB Chats for this country, as Sentinel links to it (apps/portal lib/chatsLink.ts). */
export const STANDALONE_CHATS_URL = `https://${String(import.meta.env.VITE_COUNTRY_CODE ?? 'za').toLowerCase()}.chats.cleanmybed.com/`;

const UA = navigator.userAgent;
/** Inside WhatsApp, Telegram, Instagram or Facebook's own browser: alerts and the home screen cannot work there. */
export const inAppBrowser = /FBAN|FBAV|FB_IAB|Instagram|WhatsApp|Telegram|Line\/|; wv\)/i.test(UA);
/** Chrome, Firefox, Edge or Opera on an iPhone: only Safari can add CMB Chats to the home screen. */
export const iosNotSafari = isIOS && /CriOS|FxiOS|EdgiOS|OPiOS/.test(UA);
export const isSamsungBrowser = /SamsungBrowser/i.test(UA);

/**
 * The set-up test alert was tapped on this phone (Gary, 19 Oct): Sentinel counts the phone for leads only then, so
 * set-up finishes on the tap. Kept on this phone; the open screen hears it at once.
 */
// Per login (review, 19 Oct): a second person on the same phone has not tapped a test yet.
const testKey = (userId: string) => `chats.testTapped.${userId}`;
export function testTapped(userId: string | undefined): boolean {
  if (!userId) return false;
  try { return localStorage.getItem(testKey(userId)) === '1'; } catch { return false; }
}
function markTestTapped(userId: string) {
  try { localStorage.setItem(testKey(userId), '1'); } catch { /* storage blocked: Sentinel still has it */ }
  window.dispatchEvent(new Event('chats-test-tapped'));
}

/** This device's alert registration, or null when it has none (set-up tests this device only). */
export async function thisEndpoint(): Promise<string | null> {
  try {
    const reg = await workerReady();
    return (await reg?.pushManager.getSubscription())?.endpoint ?? null;
  } catch {
    return null;
  }
}
